import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  BookOpen, UserPlus, Users, Calendar, DollarSign, CreditCard, 
  LayoutDashboard, Clock, FileText, Shield, HelpCircle, ChevronDown,
  ChevronRight, Download, MapPin, Phone, Globe, CheckCircle, ArrowLeft
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';

const ParentGuide = ({ onBack }) => {
  const [activeSection, setActiveSection] = useState('getting-started');

  const sections = [
    { id: 'getting-started', title: 'Getting Started', icon: BookOpen },
    { id: 'registration', title: 'Registration', icon: UserPlus },
    { id: 'sessions', title: 'Sessions & Schedule', icon: Calendar },
    { id: 'pricing', title: 'Pricing', icon: DollarSign },
    { id: 'payment', title: 'Payment', icon: CreditCard },
    { id: 'dashboard', title: 'Parent Dashboard', icon: LayoutDashboard },
    { id: 'credits', title: 'Credits & MC', icon: FileText },
    { id: 'membership', title: 'SCC Membership', icon: Shield },
    { id: 'faq', title: 'FAQ', icon: HelpCircle },
  ];

  const memberRates = [
    { sessions: '1x/week', perSession: '$42.30', term: '$423', discount: '-' },
    { sessions: '2x/week', perSession: '$37.65', term: '$753', discount: '11%' },
    { sessions: '3x/week', perSession: '$32.00', term: '$960', discount: '24%' },
  ];

  const nonMemberRates = [
    { sessions: '1x/week', perSession: '$47.00', term: '$470', discount: '-' },
    { sessions: '2x/week', perSession: '$41.83', term: '$836.60', discount: '11%' },
    { sessions: '3x/week', perSession: '$35.56', term: '$1,066.80', discount: '24%' },
  ];

  const sibling2Rates = [
    { sessions: '1x/week', perSession: '$33.84', term: '$338.40', discount: '20%' },
    { sessions: '2x/week', perSession: '$30.12', term: '$602.40', discount: '29%' },
    { sessions: '3x/week', perSession: '$25.60', term: '$768', discount: '39%' },
  ];

  const sibling3Rates = [
    { sessions: '1x/week', perSession: '$31.73', term: '$317.30', discount: '25%' },
    { sessions: '2x/week', perSession: '$28.24', term: '$564.80', discount: '33%' },
    { sessions: '3x/week', perSession: '$24.00', term: '$720', discount: '43%' },
  ];

  const faqItems = [
    {
      q: "How long does registration approval take?",
      a: "Usually within 1-2 business days."
    },
    {
      q: "Can I register multiple children at once?",
      a: "Yes! Add all children during registration to benefit from sibling discounts (20% for 2 children, 25% for 3 children)."
    },
    {
      q: "How long are the training sessions?",
      a: "All sessions are 1.5 hours - longer than the typical 1-hour sessions elsewhere."
    },
    {
      q: "What should my child bring?",
      a: "Water bottle (min 500ml), shin guards (mandatory), football boots, and comfortable sports attire. We provide training bibs and footballs."
    },
    {
      q: "What if my child can't attend a session?",
      a: "For illness, submit an MC through the portal to receive a session credit. Credits are only for academy-cancelled sessions (rain, etc.), not personal absences."
    },
    {
      q: "Can I add extra sessions without upgrading my tier?",
      a: "Yes! Book ad-hoc sessions at $50 (member) or $55 (non-member) per session."
    },
    {
      q: "I'm not an SCC member - can my child still join?",
      a: "Absolutely! Non-members are welcome. You can also get 5% off through Digital Membership ($126)."
    },
    {
      q: "What happens if it rains?",
      a: "Light rain - training continues. Heavy rain/lightning - session cancelled with automatic credit. Cancellations announced via Telegram and portal."
    },
    {
      q: "Can my child play in competitions?",
      a: "Yes! We participate in JSSL (open to all) and SYL (by coach selection). Game sessions count as part of your package."
    },
    {
      q: "How do I get the best rate?",
      a: "Commit to 3 sessions/week for up to 24% off, or enroll multiple siblings for up to 43% off."
    },
  ];

  const renderSection = () => {
    switch (activeSection) {
      case 'getting-started':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-display text-primary mb-4">Welcome to SCC Football Academy</h3>
              <p className="text-muted-foreground mb-4">
                SCC Football Academy is part of Singapore Cricket Club's Football Section, offering youth football development since 2015. 
                We are the only club in Singapore offering a lifetime pathway from youth to senior football.
              </p>
            </div>
            
            <Card className="bg-gradient-card gold-border">
              <CardHeader>
                <CardTitle className="text-lg">What You'll Need</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-success" /> Email address</li>
                  <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-success" /> Mobile phone number</li>
                  <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-success" /> Your child's details (birthdate, emergency contact)</li>
                  <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-success" /> SCC Membership number (if applicable)</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-primary/10 border-primary/30">
              <CardHeader>
                <CardTitle className="text-lg text-primary">Quick Start</CardTitle>
              </CardHeader>
              <CardContent>
                <ol className="space-y-2 text-muted-foreground list-decimal list-inside">
                  <li>Create an account at portal.sccfootball.com</li>
                  <li>Complete parent registration</li>
                  <li>Add your children</li>
                  <li>Select sessions (1x, 2x, or 3x per week)</li>
                  <li>Make payment</li>
                  <li>Start training!</li>
                </ol>
              </CardContent>
            </Card>
          </div>
        );

      case 'registration':
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-display text-primary mb-4">Creating Your Account</h3>
            
            <div className="space-y-4">
              <Card className="bg-gradient-card gold-border">
                <CardHeader>
                  <CardTitle className="text-lg">Step 1: Visit the Portal</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Go to <span className="text-primary font-medium">portal.sccfootball.com</span> and click "Sign Up" or "Register".
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-gradient-card gold-border">
                <CardHeader>
                  <CardTitle className="text-lg">Step 2: Enter Your Details</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-1 text-muted-foreground text-sm">
                    <li>• Full Name (as it appears on official documents)</li>
                    <li>• Email (this will be your login)</li>
                    <li>• Phone Number</li>
                    <li>• Password (minimum 8 characters)</li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="bg-gradient-card gold-border">
                <CardHeader>
                  <CardTitle className="text-lg">Step 3: Add Child Details</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-1 text-muted-foreground text-sm">
                    <li>• Child's Full Name (as per birth certificate)</li>
                    <li>• Date of Birth (determines age group)</li>
                    <li>• Gender</li>
                    <li>• Emergency Contact Name & Phone</li>
                    <li>• Medical Information (allergies, conditions)</li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="bg-gradient-card gold-border">
                <CardHeader>
                  <CardTitle className="text-lg">Step 4: Select Sessions & Pay</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-sm">
                    Choose your sessions (1x, 2x, or 3x per week), review pricing, and complete payment.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        );

      case 'sessions':
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-display text-primary mb-4">Training Schedule</h3>
            <p className="text-muted-foreground mb-4">All sessions are <span className="text-primary font-medium">1.5 hours</span> - longer than typical 1-hour sessions elsewhere.</p>
            
            <Card className="bg-gradient-card gold-border">
              <CardHeader>
                <CardTitle className="text-lg">Development / Recreational</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Tuesday</span>
                    <span className="text-card-foreground">5:30 PM - 7:00 PM (Dempsey)</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Sunday</span>
                    <span className="text-card-foreground">8:00 AM - 9:30 AM (Padang)</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-card gold-border">
              <CardHeader>
                <CardTitle className="text-lg">Competitive</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Tuesday</span>
                    <span className="text-card-foreground">5:30 PM - 7:00 PM (Dempsey)</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Wednesday</span>
                    <span className="text-card-foreground">5:30 PM - 7:00 PM (Dempsey)</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Friday</span>
                    <span className="text-card-foreground">5:30 PM - 7:00 PM (Dempsey)</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Sat-Sun</span>
                    <span className="text-card-foreground">Competitions</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-card gold-border">
              <CardHeader>
                <CardTitle className="text-lg">Girls</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Tuesday</span>
                    <span className="text-card-foreground">5:30 PM - 7:00 PM (Mixed)</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Wednesday</span>
                    <span className="text-card-foreground">5:30 PM - 7:00 PM (Girls only)</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-card gold-border">
              <CardHeader>
                <CardTitle className="text-lg">Saturday Training</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Saturday</span>
                    <span className="text-card-foreground">3:00 PM - 4:30 PM (U12, U14, U15)</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Saturday</span>
                    <span className="text-card-foreground">5:00 PM - 6:30 PM (U7, U8, U9, U10)</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-charcoal border-border">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-card-foreground">Training Locations</p>
                    <p className="text-xs text-muted-foreground mt-1">Dempsey Field: 71G Loewen Road, Singapore 248847</p>
                    <p className="text-xs text-muted-foreground">The Padang: St Andrew's Road, Singapore 178959</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case 'pricing':
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-display text-primary mb-4">2026 Term 1 Pricing</h3>
            
            <Card className="bg-gradient-card gold-border">
              <CardHeader>
                <CardTitle className="text-lg">Member Rates (Standard)</CardTitle>
                <CardDescription>Individual child term package</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-2 text-muted-foreground">Sessions</th>
                        <th className="text-right py-2 text-muted-foreground">Per Session</th>
                        <th className="text-right py-2 text-muted-foreground">Term</th>
                        <th className="text-right py-2 text-muted-foreground">Discount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {memberRates.map((rate, idx) => (
                        <tr key={idx} className="border-b border-border/50">
                          <td className="py-2 text-card-foreground">{rate.sessions}</td>
                          <td className="py-2 text-right text-card-foreground">{rate.perSession}</td>
                          <td className="py-2 text-right text-primary font-medium">{rate.term}</td>
                          <td className="py-2 text-right text-success">{rate.discount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-card gold-border">
              <CardHeader>
                <CardTitle className="text-lg">Non-Member Rates (Standard)</CardTitle>
                <CardDescription>Individual child term package</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-2 text-muted-foreground">Sessions</th>
                        <th className="text-right py-2 text-muted-foreground">Per Session</th>
                        <th className="text-right py-2 text-muted-foreground">Term</th>
                        <th className="text-right py-2 text-muted-foreground">Discount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {nonMemberRates.map((rate, idx) => (
                        <tr key={idx} className="border-b border-border/50">
                          <td className="py-2 text-card-foreground">{rate.sessions}</td>
                          <td className="py-2 text-right text-card-foreground">{rate.perSession}</td>
                          <td className="py-2 text-right text-primary font-medium">{rate.term}</td>
                          <td className="py-2 text-right text-success">{rate.discount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-card gold-border">
              <CardHeader>
                <CardTitle className="text-lg">Sibling Bundle - 2 Children</CardTitle>
                <CardDescription>Per child pricing (Member rates)</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-2 text-muted-foreground">Sessions</th>
                        <th className="text-right py-2 text-muted-foreground">Per Session</th>
                        <th className="text-right py-2 text-muted-foreground">Term (each)</th>
                        <th className="text-right py-2 text-muted-foreground">Discount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sibling2Rates.map((rate, idx) => (
                        <tr key={idx} className="border-b border-border/50">
                          <td className="py-2 text-card-foreground">{rate.sessions}</td>
                          <td className="py-2 text-right text-card-foreground">{rate.perSession}</td>
                          <td className="py-2 text-right text-primary font-medium">{rate.term}</td>
                          <td className="py-2 text-right text-success">{rate.discount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-card gold-border">
              <CardHeader>
                <CardTitle className="text-lg">Sibling Bundle - 3 Children</CardTitle>
                <CardDescription>Per child pricing (Member rates)</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-2 text-muted-foreground">Sessions</th>
                        <th className="text-right py-2 text-muted-foreground">Per Session</th>
                        <th className="text-right py-2 text-muted-foreground">Term (each)</th>
                        <th className="text-right py-2 text-muted-foreground">Discount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sibling3Rates.map((rate, idx) => (
                        <tr key={idx} className="border-b border-border/50">
                          <td className="py-2 text-card-foreground">{rate.sessions}</td>
                          <td className="py-2 text-right text-card-foreground">{rate.perSession}</td>
                          <td className="py-2 text-right text-primary font-medium">{rate.term}</td>
                          <td className="py-2 text-right text-success">{rate.discount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-charcoal border-border">
              <CardHeader>
                <CardTitle className="text-lg">Ad-Hoc Sessions</CardTitle>
                <CardDescription>For trials or extra sessions</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-card-foreground">Member: <span className="text-primary font-bold">$50</span>/session</p>
                    <p className="text-card-foreground">Non-Member: <span className="text-primary font-bold">$55</span>/session</p>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Already on a term package? Add extra sessions at ad-hoc rates when you can't commit to upgrading your tier.
                </p>
              </CardContent>
            </Card>

            <div className="glass-card rounded-lg p-4 gold-border">
              <p className="text-muted-foreground text-sm">
                <span className="text-primary font-medium">Key Notes:</span> Member discount is 5% off Non-Member rates. 
                No GST when paying directly to Football And More Limited.
              </p>
            </div>
          </div>
        );

      case 'payment':
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-display text-primary mb-4">Making Payment</h3>
            
            <Card className="bg-gradient-card gold-border">
              <CardHeader>
                <CardTitle className="text-lg">Payment Methods</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="font-medium text-card-foreground mb-1">PayNow (Recommended)</p>
                  <p className="text-sm text-muted-foreground">Scan QR code or enter UEN. Include reference number.</p>
                </div>
                <div>
                  <p className="font-medium text-card-foreground mb-1">Bank Transfer</p>
                  <p className="text-sm text-muted-foreground">Transfer to provided account. Include reference number.</p>
                </div>
                <div>
                  <p className="font-medium text-card-foreground mb-1">Credit/Debit Card</p>
                  <p className="text-sm text-muted-foreground">Secure online payment through portal.</p>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-success/10 border-success/30">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-success flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-success">Save on GST!</p>
                    <p className="text-sm text-muted-foreground">
                      No GST is charged when paying directly to Football And More Limited instead of via SCC account.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-card gold-border">
              <CardHeader>
                <CardTitle className="text-lg">After Payment</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Confirmation email sent immediately</li>
                  <li>• Payment shows as "Pending" until verified</li>
                  <li>• Admin verifies within 1-2 business days</li>
                  <li>• Status changes to "Approved" once verified</li>
                  <li>• Invoice available in Parent Dashboard</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        );

      case 'dashboard':
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-display text-primary mb-4">Parent Dashboard</h3>
            
            <Card className="bg-gradient-card gold-border">
              <CardHeader>
                <CardTitle className="text-lg">Dashboard Overview</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="font-medium text-card-foreground mb-1">Quick Stats</p>
                  <p className="text-sm text-muted-foreground">Children enrolled, upcoming sessions, credits balance, pending payments</p>
                </div>
                <div>
                  <p className="font-medium text-card-foreground mb-1">My Children</p>
                  <p className="text-sm text-muted-foreground">View enrollment status, current sessions, attendance history, credits</p>
                </div>
                <div>
                  <p className="font-medium text-card-foreground mb-1">Quick Actions</p>
                  <p className="text-sm text-muted-foreground">Add sessions, book ad-hoc, register another child</p>
                </div>
                <div>
                  <p className="font-medium text-card-foreground mb-1">Notifications</p>
                  <p className="text-sm text-muted-foreground">Announcements, session reminders, schedule changes</p>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-charcoal border-border">
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">
                  <span className="text-primary font-medium">Access:</span> Log in at portal.sccfootball.com → Click "Parent Dashboard"
                </p>
              </CardContent>
            </Card>
          </div>
        );

      case 'credits':
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-display text-primary mb-4">Credits System</h3>
            
            <Card className="bg-gradient-card gold-border">
              <CardHeader>
                <CardTitle className="text-lg">How Credits Are Earned</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-success" />
                    Rained out session → 1 session credit (automatic)
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-success" />
                    Academy cancels session → 1 session credit (automatic)
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-success" />
                    Unused game sessions → Sessions remain as credits
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-gradient-card gold-border">
              <CardHeader>
                <CardTitle className="text-lg">Credit Rules</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Session credits roll over to next term (don't expire)</li>
                  <li>• Credits automatically applied when you enroll for new term</li>
                  <li>• Use credits for ad-hoc bookings</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-destructive/10 border-destructive/30">
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">
                  <span className="text-destructive font-medium">Important:</span> No credits for personal absences (travel, other activities, etc.). 
                  This is standard practice across all football academies.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-charcoal border-border">
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">
                  <span className="text-primary font-medium">View Credits:</span> Parent Dashboard → Green badge next to child's name → Click to see full history
                </p>
              </CardContent>
            </Card>
          </div>
        );

      case 'membership':
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-display text-primary mb-4">SCC Membership</h3>
            
            <Card className="bg-gradient-card gold-border">
              <CardHeader>
                <CardTitle className="text-lg">Membership Benefits</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm mb-3">
                  SCC Members receive 5% off Non-Member rates.
                </p>
                <p className="text-muted-foreground text-sm">
                  Not a member? Access the 5% discount through <span className="text-primary">Digital Membership</span> ($126).
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-card gold-border">
              <CardHeader>
                <CardTitle className="text-lg">How to Verify Membership</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm mb-2">
                  Enter your SCC membership number during registration to receive the member discount.
                </p>
                <p className="text-muted-foreground text-sm">
                  If you're unsure of your membership number, please contact the SCC membership department.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-charcoal border-border">
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">
                  <span className="text-primary font-medium">Become an SCC Member:</span> Contact the SCC membership department or send enquiry via portal.sccfootball.com
                </p>
              </CardContent>
            </Card>
          </div>
        );

      case 'faq':
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-display text-primary mb-4">Frequently Asked Questions</h3>
            
            <Accordion type="single" collapsible className="space-y-2">
              {faqItems.map((item, idx) => (
                <AccordionItem key={idx} value={`faq-${idx}`} className="bg-gradient-card gold-border rounded-lg px-4">
                  <AccordionTrigger className="text-left text-sm text-card-foreground hover:text-primary">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-charcoal border-b border-border sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {onBack && (
                <Button variant="ghost" size="sm" onClick={onBack} className="text-muted-foreground hover:text-primary">
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Back
                </Button>
              )}
              <div>
                <h1 className="font-display text-xl text-primary">Parent Guide</h1>
                <p className="text-xs text-muted-foreground">SCC Football Academy</p>
              </div>
            </div>
            <a href="/SCC_Parent_Guide.pdf" download>
              <Button variant="outline" size="sm" className="gold-border text-primary hover:bg-primary/10">
                <Download className="h-4 w-4 mr-2" />
                Download PDF
              </Button>
            </a>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <nav className="sticky top-24 space-y-1">
              {sections.map((section) => {
                const Icon = section.icon;
                return (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left text-sm transition-colors ${
                      activeSection === section.id
                        ? 'bg-primary/10 text-primary border border-primary/30'
                        : 'text-muted-foreground hover:bg-charcoal hover:text-card-foreground'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {section.title}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Content */}
          <div className="lg:col-span-3">
            <motion.div
              key={activeSection}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              {renderSection()}
            </motion.div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-charcoal border-t border-border mt-12">
        <div className="max-w-6xl mx-auto px-6 py-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-6 text-sm text-muted-foreground">
              <a href="https://www.sccfootball.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:text-primary">
                <Globe className="h-4 w-4" />
                www.sccfootball.com
              </a>
              <span className="flex items-center gap-1">
                <Phone className="h-4 w-4" />
                8818 6675
              </span>
            </div>
            <p className="text-xs text-muted-foreground">© 2025 SCC Football Academy</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ParentGuide;
